import { useEffect, useRef, useState } from 'react'
import FilterFilled from '../../../../assets/icons/filter-filled'
import FilterOutline from '../../../../assets/icons/filter-outline'
import { IOption } from '../../../../components/SearchMultiselect'
import {
  ActionsButtons,
  Container,
  Footer,
  FormContainer,
  Header,
  OpenFiltersButton,
  TextInput,
} from './styles'
import { useLockScroll } from '../../../../hooks/use-lock-scroll'
import { FaSearchMinus, FaSearchPlus } from 'react-icons/fa'
import { GiClockwiseRotation } from 'react-icons/gi'
import { BsSearch } from 'react-icons/bs'
import {
  INITIAL_CHECKBOXES_DATA,
  INITIAL_MY_QUESTIONS_DATA,
  INITIAL_SELECTED_DATA,
  TCheckboxName,
  TSelectName,
  TSelected,
  checkboxesToParams,
  myQuestionsToParam,
  selectsToParams,
} from './data'
import { useImmer } from 'use-immer'
import Selects from './Selects'
import Checkboxes from './Checkboxes'
import SelectedFilters from './SelectedFilters'
import { useLocalStorage } from 'react-use'
import { toast } from 'react-toastify'
import { setState } from '../../../../types/setState'
import { IFiltersParams } from '../SearchQuestionsContent/types'

interface IProps {
  isLoading?: boolean

  setFiltersParams: setState<IFiltersParams>
  setTracking: setState
}

const Filters = (props: IProps) => {
  const { lock, unlock } = useLockScroll()

  const [isOpen, setIsOpen] = useState(false)
  const [isSimpleMode, setIsSimpleMode] = useLocalStorage('questoes-is-simple-mode', false)

  const [textInput, setTextInput] = useState('')
  const [selected, setSelected] = useImmer<TSelected>(INITIAL_SELECTED_DATA)
  const [checkboxes, setCheckboxes] = useImmer(INITIAL_CHECKBOXES_DATA)
  const [myQuestions, setMyQuestions] = useImmer(INITIAL_MY_QUESTIONS_DATA)

  const [activeFiltersCount, setActiveFiltersCount] = useState(0)

  const showToastOnNextChange = useRef(false)

  const onSelectChange = (selectName: TSelectName) => (value: IOption[]) =>
    setSelected((draft) => {
      draft[selectName] = value
    })

  const onCheckboxChange = (checkboxName: TCheckboxName) => (value: boolean) =>
    setCheckboxes((draft) => {
      draft[checkboxName] = value
    })

  const onSelectRemove = (selectName: TSelectName, option: IOption) => () =>
    setSelected((draft) => {
      const index = draft[selectName].findIndex((option_) => option_.value === option.value)

      if (index !== -1) draft[selectName].splice(index, 1)
    })

  const onCheckboxRemove = (checkboxName: TCheckboxName) => () =>
    setCheckboxes((draft) => {
      draft[checkboxName] = false
    })

  const onMyQuestionsRemove = (key: keyof typeof myQuestions) => () =>
    setMyQuestions((draft) => {
      draft[key] = false
    })

  const reset = () => {
    setSelected(INITIAL_SELECTED_DATA)
    setCheckboxes(INITIAL_CHECKBOXES_DATA)
    setMyQuestions(INITIAL_MY_QUESTIONS_DATA)
    setTextInput('')
  }

  const open = () => {
    setIsOpen(true)
    lock()
  }

  const close = () => {
    setIsOpen(false)
    unlock()
  }

  const applyFilters = () => {
    showToastOnNextChange.current = true

    const { params: selectsParams, count: selectsCount } = selectsToParams(selected)
    const { params: checkboxesParams, count: checkboxesCount } = checkboxesToParams(checkboxes)
    const { param: myQuestionsParam, count: myQuestionsCount } = myQuestionsToParam(myQuestions)

    const filtersCount = selectsCount + checkboxesCount + myQuestionsCount + (textInput ? 1 : 0)

    setActiveFiltersCount(filtersCount)

    props.setFiltersParams({
      search: textInput,
      ...selectsParams,
      ...checkboxesParams,
      myQuestions: myQuestionsParam,
    })

    props.setTracking({
      event: 'ApplyQuestionFilter',
      properties: {
        ...selected,
        ...checkboxesParams,
        search: textInput,
        myQuestions: myQuestionsParam,
        advanced: !isSimpleMode,
      },
    })
  }

  useEffect(() => {
    if (showToastOnNextChange.current) {
      const message =
        'Você realizou uma alteração no filtro atual. Clique em “Filtrar questões” para atualizar.'

      toast.info(message, { position: window.innerWidth > 800 ? 'top-right' : 'top-center' })

      showToastOnNextChange.current = false
    }
  }, [selected, checkboxes, textInput, myQuestions])

  return (
    <Container data-is-simple-mode={isSimpleMode}>
      <OpenFiltersButton onClick={open}>
        {activeFiltersCount > 0 ? (
          <FilterFilled className="icon" />
        ) : (
          <FilterOutline className="icon" />
        )}
        <div className="texts">
          <p className="filter">Filtrar questões</p>
          <p className="status">
            {activeFiltersCount === 0 && 'Você não tem nenhum filtro aplicado.'}
            {activeFiltersCount === 1 && 'Você tem 1 item ativo.'}
            {activeFiltersCount > 1 && `Você tem ${activeFiltersCount} itens ativos.`}
          </p>
        </div>
      </OpenFiltersButton>

      <FormContainer data-visible={isOpen}>
        <Header>
          <p className="title">Filtrar questões</p>
          <button
            className="close"
            onClick={() => {
              close()
            }}
          >
            Fechar
          </button>
        </Header>

        <TextInput>
          <BsSearch className="icon" size={16} />

          <input
            id="searchQuestion"
            type="text"
            placeholder="Enunciado ou código da questão"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
          />
        </TextInput>

        <Selects
          selected={selected}
          onSelectChange={onSelectChange}
          canceledValue={checkboxes.canceled}
          onCanceledChange={onCheckboxChange('canceled')}
          commentedValue={checkboxes.commented}
          onCommentedChange={onCheckboxChange('commented')}
        />

        <Checkboxes
          checkboxes={checkboxes}
          onCheckboxChange={onCheckboxChange}
          myQuestions={myQuestions}
          setMyQuestions={setMyQuestions}
        />

        <Footer>
          <div className="buttons">
            <div className="first">
              <button className="switch-mode" onClick={() => setIsSimpleMode(!isSimpleMode)}>
                {isSimpleMode ? (
                  <>
                    Mostrar filtro avançado <FaSearchPlus size={18} />
                  </>
                ) : (
                  <>
                    Mostrar filtro simples <FaSearchMinus size={18} />
                  </>
                )}
              </button>
            </div>

            <div className="last">
              <button className="reset" onClick={reset}>
                Limpar
                <GiClockwiseRotation size={18} />
              </button>

              <button className="filter" onClick={applyFilters} disabled={props.isLoading}>
                Filtrar questões
              </button>
            </div>
          </div>

          <SelectedFilters
            selected={selected}
            textInput={textInput}
            checkboxes={checkboxes}
            myQuestions={myQuestions}
            onSelectRemove={onSelectRemove}
            onCheckboxRemove={onCheckboxRemove}
            onTextInputRemove={() => setTextInput('')}
            onMyQuestionsRemove={onMyQuestionsRemove}
          />
        </Footer>
      </FormContainer>

      <ActionsButtons data-visible={isOpen}>
        <button
          className="filter"
          onClick={() => {
            applyFilters()
            close()
          }}
          disabled={props.isLoading}
        >
          Filtrar questões
        </button>
        <button
          className="back"
          onClick={() => {
            close()
          }}
        >
          Voltar
        </button>
      </ActionsButtons>
    </Container>
  )
}

export default Filters
